import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    filterSearch,
    type SearchHit,
    useSearchIndex,
} from '../hooks/useSearchIndex';
import { useGraphIndex } from '../hooks/useGraphIndex';
import type { AdhigaramEntry } from '../data/types';
import './SearchOverlay.css';

interface Props {
    open: boolean;
    onClose: () => void;
}

export default function SearchOverlay({ open, onClose }: Props) {
    const index = useSearchIndex();
    const graph = useGraphIndex();
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const [query, setQuery] = useState('');
    const [highlight, setHighlight] = useState(0);
    const navigate = useNavigate();
    const { search: urlSearch } = useLocation();

    const results = useMemo(() => filterSearch(index, query), [index, query]);
    const flat: SearchHit[] = useMemo(
        () => [...results.kurals, ...results.adhigarams, ...results.themes],
        [results]
    );

    // Adhigaram → first kural lookup for adhigaram-typed hits.
    const adhFirstKural = useMemo(() => {
        if (!graph.data) return new Map<number, number>();
        return new Map(
            graph.data.adhigarams.map((a: AdhigaramEntry) => [a.n, a.kural_ns[0]])
        );
    }, [graph.data]);

    // Reset & focus when opened.
    useEffect(() => {
        if (open) {
            setQuery('');
            setHighlight(0);
            setTimeout(() => inputRef.current?.focus(), 20);
        }
    }, [open]);

    // Clamp highlight into range when results change.
    useEffect(() => {
        setHighlight((h) => (flat.length === 0 ? 0 : Math.min(h, flat.length - 1)));
    }, [flat.length]);

    // Keep highlighted row visible.
    useEffect(() => {
        if (!open) return;
        const el = listRef.current?.querySelector<HTMLElement>(
            `#search-hit-${highlight}`
        );
        el?.scrollIntoView({ block: 'nearest' });
    }, [highlight, open]);

    const activate = useCallback(
        (hit: SearchHit) => {
            let path: string;
            if (hit.kind === 'kural') {
                path = `/kural/${hit.id}`;
            } else if (hit.kind === 'adhigaram') {
                const first = adhFirstKural.get(hit.id as number);
                path = first ? `/kural/${first}` : `/adhigarams`;
            } else {
                path = `/theme/${hit.id}`;
            }
            onClose();
            navigate(`${path}${urlSearch}`);
        },
        [adhFirstKural, navigate, onClose, urlSearch]
    );

    const onKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
                return;
            }
            if (flat.length === 0) return;
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setHighlight((h) => Math.min(h + 1, flat.length - 1));
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHighlight((h) => Math.max(h - 1, 0));
            } else if (e.key === 'Enter') {
                e.preventDefault();
                const hit = flat[highlight];
                if (hit) activate(hit);
            }
        },
        [flat, highlight, activate, onClose]
    );

    if (!open) return null;

    const totalCount =
        results.kurals.length + results.adhigarams.length + results.themes.length;

    return (
        <div
            className="search-overlay"
            role="presentation"
            onClick={(e) => {
                // Only close if the backdrop itself was clicked (not the panel).
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div
                className="search-overlay__panel"
                role="dialog"
                aria-modal="true"
                aria-label="Search the corpus"
                onKeyDown={onKeyDown}
            >
                <div className="search-overlay__input-wrap">
                    <span className="search-overlay__icon" aria-hidden="true">
                        ⌕
                    </span>
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setHighlight(0);
                        }}
                        placeholder="Search — try “anbu”, “அன்பு”, or a kural number"
                        className="search-overlay__input"
                        role="combobox"
                        aria-controls="search-results"
                        aria-expanded={flat.length > 0}
                        aria-activedescendant={
                            flat[highlight] ? `search-hit-${highlight}` : undefined
                        }
                        aria-autocomplete="list"
                    />
                    <button
                        type="button"
                        onClick={onClose}
                        className="search-overlay__close"
                        aria-label="Close search"
                    >
                        Esc
                    </button>
                </div>

                <div
                    ref={listRef}
                    id="search-results"
                    className="search-overlay__results"
                    role="listbox"
                >
                    {query.trim().length === 0 && (
                        <div className="search-overlay__hint">
                            Try a kural number, a theme, or an English/Tamil word from a
                            verse. Roman transliteration works too — <em>anbu</em> finds{' '}
                            <span lang="ta">அன்பு</span>.
                        </div>
                    )}
                    {query.trim().length > 0 && totalCount === 0 && (
                        <div className="search-overlay__empty">
                            No matches for “{query.trim()}”.
                        </div>
                    )}
                    <ResultGroup
                        title="Kurals"
                        hits={results.kurals}
                        baseIndex={0}
                        highlight={highlight}
                        onActivate={activate}
                    />
                    <ResultGroup
                        title="Adhigarams"
                        hits={results.adhigarams}
                        baseIndex={results.kurals.length}
                        highlight={highlight}
                        onActivate={activate}
                    />
                    <ResultGroup
                        title="Themes"
                        hits={results.themes}
                        baseIndex={results.kurals.length + results.adhigarams.length}
                        highlight={highlight}
                        onActivate={activate}
                    />
                </div>
            </div>
        </div>
    );
}

function ResultGroup({
    title,
    hits,
    baseIndex,
    highlight,
    onActivate,
}: {
    title: string;
    hits: SearchHit[];
    baseIndex: number;
    highlight: number;
    onActivate: (hit: SearchHit) => void;
}) {
    if (hits.length === 0) return null;
    return (
        <section className="search-group" aria-label={title}>
            <h3 className="search-group__title">
                {title} <span className="search-group__count">({hits.length})</span>
            </h3>
            <ul className="search-group__list">
                {hits.map((hit, i) => {
                    const globalIdx = baseIndex + i;
                    const isActive = globalIdx === highlight;
                    return (
                        <li key={`${hit.kind}-${hit.id}`}>
                            <button
                                type="button"
                                id={`search-hit-${globalIdx}`}
                                role="option"
                                aria-selected={isActive}
                                className={`search-hit${isActive ? ' is-active' : ''}`}
                                onMouseEnter={() => {
                                    /* Hover doesn't scroll into view; only sets active via a data attr */
                                }}
                                onClick={() => onActivate(hit)}
                            >
                                <span className="search-hit__primary">
                                    <span>{hit.primary}</span>
                                    {hit.primaryTa && (
                                        <span lang="ta" className="search-hit__primary-ta">
                                            {hit.primaryTa}
                                        </span>
                                    )}
                                </span>
                                {hit.sub && <span className="search-hit__sub">{hit.sub}</span>}
                            </button>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}
